/* eslint-disable react/no-unescaped-entities */
import { useContext, useEffect, useState } from 'react';

import axios from 'axios';
import { PasswordValidator } from 'components/layout/password-validator/password-validator';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import useLeadsMixPanel from 'pages/leads/hooks/use-leads-mixpanel';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';

import { Box, Button, InputPassword, Label } from '@olist/united';

import { API_URLS, MIXPANEL_EVENTS } from '../../constants';
import { RootContext } from '../leads-provider';

export interface IPasswordFormProps {
  setShouldShowErrors: (value: boolean) => void;
}

const PasswordForm = ({ setShouldShowErrors }: IPasswordFormProps) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const mixpanelInstance = useLeadsMixPanel();

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);
  };

  const {
    expireLeadCookiesAfterSeconds,
    leadCategory,
    leadIdCookie,
    leadProfileCookie,
    leadRequisites,
    leadRevenue,
    setLeadTotalFields,
  } = useContext(RootContext);

  const [{ error, loading: isLoading, value: responseData }, submitPassword] = useAsyncFn(
    async (data) => {
      return axios.post(API_URLS.postAccountCreation, {
        lead_id: leadIdCookie,
        password: data.password,
      });
    }
  );

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitPassword({ password });
  };

  const registerMixpanelLead = (sellerId: string) => {
    mixpanelInstance?.alias(sellerId, leadIdCookie);
    mixpanelInstance?.identify(sellerId);
    mixpanelInstance?.track(MIXPANEL_EVENTS.SIGNUP_COMPLETE);
  };

  useEffect(() => {
    const requisites = leadRequisites ? JSON.parse(leadRequisites) : {};
    if (responseData) {
      const { data } = responseData as any;
      registerMixpanelLead(data.id);
      expireLeadCookiesAfterSeconds();
      setLeadTotalFields(1);

      sendGTMEvent('lead_pq', {
        user_id: leadProfileCookie?.email,
        categoria: leadCategory,
        faturamento: leadRevenue,
        ...requisites,
      });

      window.location.href = '/';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseData]);

  useEffect(() => {
    if (error && !isLoading) {
      setShouldShowErrors(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  return (
    <form onSubmit={onSubmit}>
      <Box mt={4} maxWidth="288px" data-testid="password-form">
        <Label htmlFor="password">{t`create new password`}</Label>
        <InputPassword
          id="password"
          data-testid="input-password"
          label="password"
          value={password}
          onChange={handlePasswordChange}
          placeholder={t`password`}
          required
        />
        <Box mt={3}>
          <PasswordValidator onValid={(value) => setIsPasswordValid(value)} password={password} />
        </Box>
        <Button
          mt={3}
          variation="primary"
          loading={isLoading}
          disabled={!isPasswordValid || isLoading}
          type="submit"
        >
          {t`save and join olist store`}
        </Button>
      </Box>
    </form>
  );
};

export default PasswordForm;
